import SmartLabelIcon from "@/components/SmartLabelIcon";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import EventCardMailTile from "@/components/widgets/meetings/EventCardMailTile";
import { useVistassistantMail } from "@/contexts/VistassistantMailContext";
import Email, {
    MailComponentSize,
    MailsListId,
} from "@/domain/entities/emails/Email";
import MailSource from "@/domain/enums/MailSource";
import { isSameCopy } from "@/lib/mailIdentity";
import { cn } from "@/lib/utils";
import mailStore, { setOpenedMail } from "@/stores/mailStore";
import { useSdk } from "@cs_teamwaydev/mailvista-sdk_react";
import { useStore } from "@tanstack/react-store";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import CategoriesList from "./CategoriesList";
import GroupAvatars from "./GroupAvatars";
import { FocusTarget, MailAction } from "./MailAction";
import MailAISummary from "./MailAISummary";
import MailDate from "./MailDate";
import MailIcons from "./MailIcons";
import MailTitle from "./MailTitle";
import selectTileImageAttachment from "./selectTileImageAttachment";
import TreatedTag from "./TreatedTag";

type MailTileProps = {
    mail: Email;
    onSelectChange?: (checked: boolean, mail: Email) => void;
    size: MailComponentSize;
    listId: MailsListId;
    setMailInputExpanded: (
        mailInputExpanded: boolean,
        focusTarget?: FocusTarget,
    ) => void;
    setSelectedMails: Dispatch<SetStateAction<Email[]>>;
    selectedMails: Email[];
};

const MailTile = ({
    mail,
    onSelectChange,
    size,
    listId,
    setMailInputExpanded,
    setSelectedMails,
    selectedMails,
}: MailTileProps) => {
    const { isVistassistantResult, onMailClick } = useVistassistantMail();
    const { mailManager } = useSdk();
    const [keepActionsVisible, setKeepActionsVisible] = useState(false);

    const checked = useMemo(
        () => selectedMails.includes(mail),
        [selectedMails, mail],
    );

    const openedMail = useStore(mailStore, (state) => state.openedMail);
    const isOpened = !!openedMail && isSameCopy(openedMail, mail);
    const variant = mail.getMailStatus();
    const isSentList = listId === MailSource.SentItems;

    const linkedEvent = mail.linkedEventDetails;

    const imageAttachment = selectTileImageAttachment(mail.attachments);
    // Clé de dépendance de l'effet : l'id (stable) plutôt que l'instance — le store peut recréer
    // les objets EmailAttachment à un re-render, ce qui re-déclencherait l'effet pour rien.
    const imageAttachmentId = imageAttachment?.id;

    // Preview de la vignette : depuis AB#1727 elle n'est PLUS servie par défaut (`attachment.preview` vide)
    // → on la récupère À LA DEMANDE via le SDK (best-effort ; le SDK dédupe/cache par session). Tant qu'elle
    // n'est pas résolue (ou si elle échoue), `imagePreviewUrl` reste null → on n'affiche AUCUNE image (jamais
    // de `<img src="">` cassé). Cf. DocumentPreview (même resolver).
    const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;

        // Reset systématique AVANT le fetch : une tuile recyclée (changement d'attachment sans
        // re-montage par clé) ne doit pas afficher brièvement la preview périmée du précédent.
        setImagePreviewUrl(null);

        if (!imageAttachmentId) {
            return;
        }

        mailManager
            .getAttachmentPreview({
                uniqueMailvistaId: mail.uniqueMailvistaId,
                attachmentId: imageAttachmentId,
            })
            .then((url) => {
                if (!cancelled) setImagePreviewUrl(url);
            })
            .catch(() => {
                // Échec permanent assumé (revue PR #1071) : le slot réservé (h-37) reste vide sur
                // les tuiles LARGE/NARROW — pas de layout shift, jamais d'<img> cassé.
                if (!cancelled) setImagePreviewUrl(null);
            });

        return () => {
            cancelled = true;
        };
    }, [imageAttachmentId, mail.uniqueMailvistaId, mailManager]);

    const gotHeader =
        (size === MailComponentSize.LARGE ||
            size === MailComponentSize.NARROW) &&
        imageAttachment;

    const gotFooter = size === MailComponentSize.NARROW && !imageAttachment;

    const shouldHaveExtraInfos =
        size === MailComponentSize.LARGE ||
        size === MailComponentSize.WIDE ||
        gotFooter;

    const handleCheckboxChange = (isChecked: boolean) => {
        onSelectChange?.(Boolean(isChecked), mail);
    };

    const mainContent = (
        <div
            className={cn(
                "col-start-1 row-start-1 flex h-37 min-w-0 flex-col gap-3 overflow-hidden",
                gotHeader && "row-start-2",
            )}
        >
            <MailDate sentDateTime={mail.sentDateTime} />
            <GroupAvatars mail={mail} />
            <div className="flex items-center gap-2">
                {mail.aiDetails && mail.smartLabel && !isSentList && (
                    <SmartLabelIcon smartLabel={mail.smartLabel} />
                )}
                <MailTitle isRead={mail.isRead} mail={mail} />
            </div>
            <MailAISummary
                isSentFolder={isSentList}
                mail={mail}
                variant="long"
            />
        </div>
    );

    const imageContent = (
        <div
            className={`col-start-1 row-start-1 flex h-37 w-full items-center justify-center overflow-hidden ${
                size === MailComponentSize.LARGE ? "col-span-2" : "col-span-1"
            }`}
        >
            {imagePreviewUrl && (
                <img
                    alt="attachment"
                    className="max-h-full max-w-full rounded-lg object-contain"
                    src={imagePreviewUrl}
                />
            )}
        </div>
    );

    const getExtraInfosContent = () => {
        if (linkedEvent) return <EventCardMailTile event={linkedEvent} />;

        // Ne rendre la vignette QUE s'il y a une image affichable (AB#1725) : sans ce garde, `imageContent`
        // était rendu même sans image → `<img>` sans src exploitable (voire `src=""` pour une image inline
        // sans preview) → vignette cassée.
        return !gotHeader && imageAttachment ? imageContent : null;
    };

    const extraInfosContent = getExtraInfosContent();

    const extraInfos = shouldHaveExtraInfos && (
        <div
            className={cn(
                "flex h-37 w-full flex-col justify-between gap-2 overflow-hidden",
                gotFooter ? "col-start-1" : "col-start-2",
                gotHeader || gotFooter ? "row-start-2 h-45" : "row-start-1",
            )}
        >
            {extraInfosContent}
        </div>
    );

    return (
        <Card
            className={cn(
                "group relative gap-x-4 gap-y-2 overflow-hidden",
                checked && "border-primary-darker",
            )}
            data-mail-id={mail.id}
            display="grid"
            forceHover={keepActionsVisible}
            onClick={() => {
                if (isVistassistantResult && onMailClick) {
                    onMailClick(mail, listId);
                } else if (!isOpened) {
                    setMailInputExpanded(false);
                    setOpenedMail(mail);
                }
            }}
            opened={isOpened}
            size={size}
            variant={variant}
        >
            {gotHeader && imageContent}

            {extraInfos}

            {mainContent}

            <div
                className={cn(
                    "flex h-6 min-w-0 items-center justify-between gap-2 overflow-hidden",
                    shouldHaveExtraInfos && "col-span-2",
                )}
            >
                <div className="flex min-w-0 items-center justify-end gap-2 overflow-hidden">
                    <MailIcons mail={mail} />

                    {!isSentList &&
                        (mail.isUserProcessed ? (
                            <TreatedTag />
                        ) : (
                            <CategoriesList mail={mail} />
                        ))}
                </div>
                <Checkbox
                    checked={checked}
                    className="h-5 w-5 shrink-0"
                    onCheckedChange={handleCheckboxChange}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.stopPropagation();
                        }
                    }}
                />
            </div>

            {selectedMails.length === 0 && (
                <div
                    className={cn(
                        "absolute right-2 top-2",
                        keepActionsVisible ? "flex" : "hidden group-hover:flex",
                    )}
                >
                    <MailAction
                        className="my-auto"
                        listId={listId}
                        mail={mail}
                        onTooltipOpenChange={setKeepActionsVisible}
                        setMailInputExpanded={setMailInputExpanded}
                        setSelectedMails={setSelectedMails}
                    />
                </div>
            )}
        </Card>
    );
};

export default MailTile;
